/**
 * TierBadge Component
 *
 * Part of Story 3.1: Tier Badge Component
 * Epic 3: Frontend UI Components
 *
 * Displays the user's current subscription tier with visual styling,
 * tooltip on hover, and click navigation to billing settings.
 *
 * Acceptance Criteria:
 * - AC#1: Accepts tier and size props
 * - AC#2: Displays tier icon and name
 * - AC#3: Color-coded by tier
 * - AC#4: Hover tooltip shows tier details
 * - AC#5: Click navigates to /settings/billing
 * - AC#6: Responsive sizing
 * - AC#7: Accessible with aria-label and keyboard support
 */

import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Tier, TierBadgeProps, TierStyle } from '@/types/billing';
import { useTierData } from '@/hooks/useTierData';

/**
 * Tier style configurations
 * Defines icon, name, and Tailwind color classes for each tier
 */
const TIER_STYLES: Record<Tier, TierStyle> = {
  solo: {
    icon: '👤',
    name: 'Solo',
    bgColor: 'bg-blue-100',
    textColor: 'text-blue-800',
    borderColor: 'border-blue-300',
  },
  agency_starter: {
    icon: '🏢',
    name: 'Agency Starter',
    bgColor: 'bg-purple-100',
    textColor: 'text-purple-800',
    borderColor: 'border-purple-300',
  },
  agency_pro: {
    icon: '⭐',
    name: 'Agency Pro',
    bgColor: 'bg-amber-100',
    textColor: 'text-amber-800',
    borderColor: 'border-amber-300',
  },
  enterprise: {
    icon: '👑',
    name: 'Enterprise',
    bgColor: 'bg-white/[0.04]',
    textColor: 'text-gray-800',
    borderColor: 'border-white/[0.08]',
  },
};

/**
 * Size variant styles
 * Defines padding and font size for small, medium, and large variants
 */
const SIZE_STYLES = {
  small: 'px-2 py-1 text-xs',
  medium: 'px-3 py-1.5 text-sm',
  large: 'px-4 py-2 text-base',
};

/**
 * TierBadge Component
 *
 * A badge component that displays the current subscription tier with
 * appropriate styling, tooltip information, and navigation on click.
 *
 * @example
 * ```tsx
 * // Basic usage in navigation
 * <TierBadge tier="solo" size="medium" />
 *
 * // With responsive sizing
 * <TierBadge tier="agency_starter" className="size-small md:size-medium" />
 *
 * // Custom click handler
 * <TierBadge tier="enterprise" onClick={() => console.log('Clicked!')} />
 * ```
 */
export const TierBadge = React.memo<TierBadgeProps>(
  ({ tier, size = 'medium', onClick, className = '' }) => {
    const navigate = useNavigate();
    const { data: tierData } = useTierData();

    // Get tier style configuration
    const style = TIER_STYLES[tier];
    const sizeClass = SIZE_STYLES[size];

    /**
     * Handle badge click - navigate to billing settings or call custom handler
     */
    const handleClick = useCallback(() => {
      if (onClick) {
        onClick();
      } else {
        navigate('/settings/billing');
      }
    }, [onClick, navigate]);

    /**
     * Generate aria-label for accessibility
     * Includes tier name, brand count, agent count, and execution quota
     */
    const ariaLabel = tierData?.brands
      ? `Current tier: ${style.name}. ${tierData.brands.current} of ${tierData.brands.max} brands, ${tierData.allowed_agents?.length || 0} agents, ${tierData.quota_limit} executions per month. Click to manage billing.`
      : `Current tier: ${style.name}. Click to manage billing.`;

    /**
     * Generate tooltip content
     * Shows brand limit, agent count, and execution quota
     */
    const tooltipContent = tierData?.brands
      ? `${tierData.brands.max} brand${tierData.brands.max > 1 ? 's' : ''}, ${tierData.allowed_agents?.length || 0} agents, ${tierData.quota_limit} executions/month`
      : `${style.name} tier`;

    return (
      <div className="relative group">
        <button
          type="button"
          onClick={handleClick}
          aria-label={ariaLabel}
          className={`
            inline-flex items-center gap-1.5 rounded-lg border
            ${style.bgColor} ${style.textColor} ${style.borderColor}
            ${sizeClass}
            font-semibold transition-all duration-200
            hover:shadow-md hover:scale-105 hover:ring-2 hover:ring-offset-1 hover:ring-${style.borderColor.replace('border-', '')}
            focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-${style.borderColor.replace('border-', '')}
            cursor-pointer
            ${className}
          `}
          tabIndex={0}
        >
          <span className="flex-shrink-0" aria-hidden="true">
            {style.icon}
          </span>
          <span className="font-semibold">{style.name}</span>
        </button>

        {/* Tooltip - appears on hover */}
        <div
          className="
            absolute z-50 left-1/2 -translate-x-1/2 bottom-full mb-2
            px-3 py-2 text-xs font-medium text-white bg-gray-900 rounded-lg shadow-lg
            opacity-0 group-hover:opacity-100
            invisible group-hover:visible
            transition-all duration-200
            pointer-events-none
            whitespace-nowrap
          "
          role="tooltip"
        >
          {tooltipContent}
          {/* Tooltip arrow */}
          <div
            className="absolute top-full left-1/2 -translate-x-1/2 -mt-px"
            aria-hidden="true"
          >
            <div className="border-4 border-transparent border-t-gray-900" />
          </div>
        </div>
      </div>
    );
  }
);

TierBadge.displayName = 'TierBadge';
